import { ROUTES } from "../../constants/routes";
import { Component, inject } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { COMPLIANCE_FAQS } from "../../constants/compliance";
import { breadcrumbSchema, faqSchema } from "../../helpers/structured-data";

interface FaqGroup {
  title: string;
  faqs: { question: string; answer: string }[];
}

@Component({
  selector: "app-faq",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./faq.html",
  styleUrl: "./faq.scss"
})
export class Faq {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);

  groups: FaqGroup[] = [
    {
      title: "About the service",
      faqs: [
        {
          question: "What is VerifyDeceased?",
          answer:
            "VerifyDeceased is a deceased identity verification service. You submit identities and we check them against fresh, confirmed nationwide death records. It helps banks, lenders, employers, insurers, government agencies and collections teams stop fraud committed with deceased identities."
        },
        {
          question: "Who is VerifyDeceased for?",
          answer:
            "Any team that needs to know whether a person is deceased before they act. That includes fraud, compliance, underwriting, claims, background screening and collections teams across financial services, insurance, employment and the public sector."
        },
        {
          question: "How is VerifyDeceased different from other death data providers?",
          answer:
            "Speed. Our records are added weekly and often reflect a passing within seven days, while many sources take two to three months to update. That freshness is what lets you catch fraud while it is still happening."
        },
        {
          question: "Is VerifyDeceased part of LeadCruncher?",
          answer:
            "Yes. VerifyDeceased is a product built by LeadCruncher, a US property and consumer data platform with more than 160 million records. We use that data foundation and infrastructure to deliver fresh, confirmed deceased records at scale."
        },
        {
          question: "Do you offer a real time API and file uploads?",
          answer:
            "Both. You can scrub a list by uploading a file in your dashboard, or call our REST API to verify a single identity in real time inside your own onboarding, origination or claims workflow."
        }
      ]
    },
    {
      title: "Data and coverage",
      faqs: [
        {
          question: "Where does the death data come from?",
          answer:
            "We combine state and county vital records and registrar filings, the Social Security Administration Death Master File accessed through the certification program administered by NTIS, published obituaries and funeral home notices, cemetery and interment records, and probate and estate court filings. Confirmed records are then enriched with last known property and mailing address data from our parent company LeadCruncher, a US property data platform covering more than 160 million records."
        },
        {
          question: "Is this just the SSA Death Master File?",
          answer:
            "No. The Death Master File is one input, and on its own it is both incomplete and slow. Obituaries, funeral notices and registrar filings usually surface a death well before it reaches a federal file, which is why our records are typically available within a week rather than two to three months."
        },
        {
          question: "How do you avoid false positives on living consumers?",
          answer:
            "We corroborate a death against supporting evidence before the record is published, and we resolve identity using date of birth and location rather than name alone. Every match returns a confidence score so you can set your own threshold, and we recommend a review step before any irreversible action such as closing an account."
        },
        {
          question: "How current is the data?",
          answer:
            "New confirmed records are added every week. In many cases a death is reflected within seven days, which is significantly faster than traditional death data sources."
        },
        {
          question: "What states are covered?",
          answer:
            "Coverage is nationwide across all 50 states and the District of Columbia."
        },
        {
          question: "What information do I get back on a match?",
          answer:
            "A confirmed match returns the date of death, age at death, last known property or mailing address, the state and a confidence score for the match."
        },
        {
          question: "How accurate is the matching?",
          answer:
            "Every match includes a confidence score so your team can decide how to act on it. Providing more detail on each record, such as date of birth or last known address, raises confidence and reduces false positives. High confidence matches can be actioned automatically, while borderline matches can be routed for review."
        },
        {
          question: "Do you cover recent deaths as well as historical ones?",
          answer:
            "Yes. Because records are refreshed weekly you get very recent passings, which is where most fraud happens, alongside a deep historical base of confirmed deceased records."
        }
      ]
    },
    {
      title: "Using the product",
      faqs: [
        {
          question: "How do I verify records?",
          answer:
            "Two ways. Upload a file through your dashboard for a batch scrub, or call our REST API to verify a single identity in real time or submit records in bulk. You choose what fits your workflow."
        },
        {
          question: "What fields do I need to submit?",
          answer:
            "At minimum a first and last name. Adding date of birth, Social Security number or last known address increases the match confidence and reduces false positives."
        },
        {
          question: "How fast are results?",
          answer:
            "A single API verification returns in milliseconds. Batch files are processed quickly and you are notified when a large job is complete."
        },
        {
          question: "What file formats can I upload?",
          answer:
            "CSV and Excel files are both supported, and there is no hard size limit. Map your columns once and we handle the rest, returning a results file you can download or push downstream."
        },
        {
          question: "Can I integrate VerifyDeceased with my existing systems?",
          answer:
            "Yes. The REST API is built to drop into account opening, loan origination, claims and collections systems. Send one identity or a batch, receive a structured JSON response, and act on it in your own tools."
        }
      ]
    },
    {
      title: "Pricing",
      faqs: [
        {
          question: "How much does verification cost?",
          answer:
            "Standard verification is four cents per record. When you verify 100,000 or more records in a month, bulk pricing of one cent per record applies. There are no contracts, minimums or setup fees."
        },
        {
          question: "Do I pay for records that are not matched?",
          answer:
            "You only pay for records we confirm as deceased. Records that return no match are not billed."
        },
        {
          question: "What is free, exactly?",
          answer:
            "Creating an account is free and no credit card is required. There is no subscription, no setup fee, no minimum and no contract, so an account costs nothing until you run records. You add funds when you are ready and are charged $0.04 per confirmed match."
        },
        {
          question: "Is there a free trial or a free credit?",
          answer:
            "There is no fixed free credit. Because you are only charged for confirmed matches, you can create an account and run a small sample list to check data quality and match rate, and you will only pay for the deaths we actually verify. For a larger structured pilot, contact sales and we will set one up."
        },
        {
          question: "How am I billed?",
          answer:
            "Billing is based on the number of records we confirm as deceased. There are no contracts, minimums or setup fees, so your cost scales directly with the value you get. Volume above 100,000 records a month automatically qualifies for bulk pricing."
        }
      ]
    },
    {
      title: "Security and compliance",
      faqs: [
        ...COMPLIANCE_FAQS,
        {
          question: "Who can access my account and results?",
          answer:
            "Access is limited to the users you authorize, protected by authentication and role based permissions. Data is encrypted in transit and at rest, and access is logged."
        },
        {
          question: "How is the data used for compliance?",
          answer:
            "Customers use our results to support fraud prevention, KYC, claims validation, program integrity and deceased suppression. You remain responsible for how results are used within the regulations that apply to your industry."
        }
      ]
    }
  ];

  get allFaqs(): { question: string; answer: string }[] {
    return this.groups.flatMap(group => group.faqs);
  }

  slug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  ngOnInit(): void {
    this.seo.apply({
      title: "Deceased Verification FAQ",
      description:
        "Answers on how matching works, where our death data comes from, coverage, what is free, SOC 2 and GLBA Safeguards compliance, data retention and API access.",
      path: "/faq",
      keywords:
        "death verification FAQ, deceased verification questions, how does death verification work, death data sources, death data coverage, death verification pricing, GLBA safeguards death data, SOC 2 death verification",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "FAQ", path: "/faq" }
        ]),
        faqSchema(this.allFaqs)
      ]
    });
  }
}
